import _ from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf, Renderer, RendererOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ErrorWithData} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import FrontImgSrc from '../../../../assets/images/frontLogo16.svg';
import HubspotImgSrc from '../../../../assets/images/integrations/hubspot16.svg';
import SalesforceImgSrc from '../../../../assets/images/integrations/salesforce16.svg';
import {computeCustomFieldIconName} from '../../../../helpers/customFieldViewHelpers';
import {
  CrmSyncAccountDefaultFieldIdsEnum,
  CrmSyncMappingCustomFieldTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../models/api/crmSyncModel';
import {CustomFieldModel, CustomFieldsEnum} from '../../../../models/api/customFieldModel';
import {ResourceMap} from '../../../../store/appTypes';
import {MappingHeader, StyledHeaderCellWrapperDiv} from '../../../common/mapping/mappingHeader';
import {CrmMappingRow} from './crmMappingRow';
import {CrmMappingRowField} from './crmMappingRowField';
import {
  CrmToFrontCustomFieldMapping,
  OnUpdateMappedFieldHandler,
  renderDefaultCustomFieldName,
} from './crmSyncHelpers';
import {CrmCustomFieldDropdownContainer} from './dropdowns/crmCustomFieldDropdownContainer';

/*
 * Props.
 */

type CrmMappingAddFieldDropdownRenderer = (
  popoverProps: PopoverTransferProps,
  crmFieldId?: string,
) => ReactNode;

export interface CrmMappingProps {
  source: CrmSyncSourcesEnum;
  mappedFields: ReadonlyArray<CrmToFrontCustomFieldMapping>;
  requiredFields: ReadonlyArray<CrmToFrontCustomFieldMapping>;
  customFields: ResourceMap<CustomFieldModel>;
  isDisabled?: boolean;
  isLoading?: boolean;
  isAdmin?: boolean;
  onUpdateMappedField: OnUpdateMappedFieldHandler;
  onRemoveMappedField: HandlerOf<string>;
  computeCrmCustomFieldIcon: (crmCustomFieldId: string) => IconName | undefined;
  computeFrontTypesFromCrmCustomField: (
    crmCustomFieldId: string,
  ) => ReadonlyArray<CustomFieldsEnum | CrmSyncMappingCustomFieldTypesEnum>;
  computeCrmCustomFieldName: (crmCustomFieldId: string) => string | undefined;
  renderAddFieldDropdown: CrmMappingAddFieldDropdownRenderer;
  renderCrmCustomFieldMissingError: Renderer;
  renderCrmPlaceholder: Renderer;
}

/*
 * Style.
 */

const StyledCrmMappingWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 32px 1fr 32px;
  row-gap: 8px;
`;

const StyledButton = styled(Button)`
  display: inline-flex;
  margin-left: -13px;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  display: inline-block;
`;

/*
 * Component.
 */

export const CrmMapping: FC<CrmMappingProps> = (props) => {
  const {requiredFields, mappedFields, source, isLoading, isDisabled, isAdmin, renderAddFieldDropdown} =
    props;

  if (isLoading) {
    return (
      <StyledCrmMappingWrapperDiv>
        {renderHeader(source, isLoading)}
        <CrmMappingRow isLoading />
        <CrmMappingRow isLoading />
      </StyledCrmMappingWrapperDiv>
    );
  }
  return (
    <StyledCrmMappingWrapperDiv>
      {renderHeader(source, isLoading)}
      {renderFields(requiredFields, props, true)}
      {renderFields(mappedFields, props)}
      {renderAddFieldButton(Boolean(!isAdmin || isDisabled), renderAddFieldDropdown)}
    </StyledCrmMappingWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderAddFieldButton(isDisabled: boolean, renderAddFieldDropdown: RendererOf<PopoverTransferProps>) {
  return (
    <div>
      <StyledPopoverCoordinator
        isDisabled={isDisabled}
        renderButton={(hasHover) => (
          <StyledButton variant={ButtonVariantsEnum.LINK} hasHover={hasHover} isDisabled={isDisabled}>
            <Trans id="accounts-crm-dialog-sync-mapping-add-field">+ Add field</Trans>
          </StyledButton>
        )}
        renderModal={renderAddFieldDropdown}
      />
    </div>
  );
}

function renderFields(
  fields: ReadonlyArray<CrmToFrontCustomFieldMapping>,
  props: CrmMappingProps,
  isReadOnly?: boolean,
) {
  const {
    isDisabled,
    requiredFields,
    mappedFields,
    source,
    isAdmin,
    onRemoveMappedField,
    renderAddFieldDropdown,
    onUpdateMappedField,
    computeFrontTypesFromCrmCustomField,
    renderCrmCustomFieldMissingError,
    renderCrmPlaceholder,
  } = props;
  return fields.map((field) => {
    const {
      frontCustomFieldName,
      frontCustomFieldIcon,
      crmCustomFieldName,
      crmCustomFieldIcon,
      isFrontCustomFieldMissing,
      isCrmCustomFieldMissing,
    } = computeMappedFieldDetails(field, props);

    const allSelectedCustomFieldIds = _([...requiredFields, ...mappedFields])
      .map((mappingField) => mappingField.customFieldId)
      .compact()
      .value();

    return (
      <CrmMappingRow
        key={field.crmFieldId}
        isReadOnly={isReadOnly}
        isDisabled={isDisabled}
        isAdmin={isAdmin}
        onRemove={() => onRemoveMappedField(field.crmFieldId)}
        renderSourceField={() =>
          renderCrmFieldDropdown(
            field.crmFieldId,
            renderAddFieldDropdown,
            renderCrmCustomFieldMissingError,
            renderCrmPlaceholder,
            crmCustomFieldIcon,
            crmCustomFieldName,
            isReadOnly,
            !isAdmin || isDisabled,
            isCrmCustomFieldMissing,
          )
        }
        renderFrontField={() => (
          <CrmCustomFieldDropdownContainer
            source={source}
            types={computeFrontTypesFromCrmCustomField(field.crmFieldId)}
            selectedCustomFieldId={field.customFieldId}
            frontCustomFieldIcon={frontCustomFieldIcon}
            frontCustomFieldName={frontCustomFieldName}
            crmCustomFieldName={crmCustomFieldName}
            isReadOnly={isReadOnly}
            isDisabled={!isAdmin || isDisabled}
            allSelectedCustomFieldIds={allSelectedCustomFieldIds}
            onSelect={(customFieldId) => onUpdateMappedField(field.crmFieldId, customFieldId)}
            isFrontCustomFieldMissing={isFrontCustomFieldMissing}
          />
        )}
      />
    );
  });
}

function renderCrmFieldDropdown(
  crmFieldId: string,
  renderAddFieldDropdown: CrmMappingAddFieldDropdownRenderer,
  renderCrmCustomFieldMissingError: Renderer,
  renderCrmPlaceholder: Renderer,
  crmCustomFieldIcon?: IconName,
  crmCustomFieldName?: string,
  isReadOnly?: boolean,
  isDisabled?: boolean,
  isCrmCustomFieldMissing?: boolean,
) {
  return (
    <PopoverCoordinator
      isDisabled={isReadOnly || isDisabled}
      renderButton={() => (
        <CrmMappingRowField
          iconName={crmCustomFieldIcon}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
          hasError={isCrmCustomFieldMissing}
          errorMessage={renderCrmCustomFieldMissingError()}
        >
          {crmCustomFieldName ?? renderCrmPlaceholder()}
        </CrmMappingRowField>
      )}
      renderModal={(popoverProps) => renderAddFieldDropdown(popoverProps, crmFieldId)}
    />
  );
}

function computeMappedFieldDetails(field: CrmToFrontCustomFieldMapping, props: CrmMappingProps) {
  const {customFields, computeCrmCustomFieldName, computeCrmCustomFieldIcon} = props;
  let isCrmCustomFieldMissing = false;
  let isFrontCustomFieldMissing = false;

  // Compute crm information.
  const crmCustomFieldName = computeCrmCustomFieldName(field.crmFieldId);
  const crmCustomFieldIcon = computeCrmCustomFieldIcon(field.crmFieldId);
  // If we fail to find crm custom field information, return early and do not render.
  if (!crmCustomFieldName || !crmCustomFieldIcon) {
    isCrmCustomFieldMissing = true;
  }

  if (!field.customFieldId) {
    return {
      crmCustomFieldName,
      crmCustomFieldIcon,
      isCrmCustomFieldMissing,
    };
  }

  const frontCustomFieldName = renderCustomFieldName(field.customFieldId, customFields);
  const frontCustomFieldIcon = computeFrontCustomFieldIcon(field.customFieldId, customFields);
  // If we fail to find custom field information, return early and do not render.
  if (!frontCustomFieldName || !frontCustomFieldIcon) {
    isFrontCustomFieldMissing = true;
  }

  return {
    frontCustomFieldName,
    frontCustomFieldIcon,
    crmCustomFieldName,
    crmCustomFieldIcon,
    isCrmCustomFieldMissing,
    isFrontCustomFieldMissing,
  };
}

function renderCustomFieldName(customFieldId: number, customFields: ResourceMap<CustomFieldModel>) {
  if (customFieldId >= 0) {
    const customField = customFields[customFieldId];
    if (!customField) {
      return undefined;
    }
    return customField.name;
  }

  // We are rendering a default field that is not a true custom field.
  return renderDefaultCustomFieldName(customFieldId);
}

function computeFrontCustomFieldIcon(
  customFieldId: number,
  customFields: ResourceMap<CustomFieldModel>,
): IconName | undefined {
  if (customFieldId >= 0) {
    const customField = customFields[customFieldId];
    if (!customField) {
      return undefined;
    }
    return computeCustomFieldIconName(customField.type, VisualSizesEnum.SMALL);
  }

  // We are rendering a default field that is not a true custom field.
  switch (customFieldId) {
    case CrmSyncAccountDefaultFieldIdsEnum.DOMAINS:
      return 'mentionSmall';
    case CrmSyncAccountDefaultFieldIdsEnum.NAME:
    case CrmSyncAccountDefaultFieldIdsEnum.DESCRIPTION:
      return 'contentSmall';
    default: {
      // We should only get back the pre-defined fields in CrmSyncDefaultFieldMappingIdsEnum.
      reportToBugsnag(
        new ErrorWithData('[CRM Account Sync] An unknown default field id was passed in.', {customFieldId}),
      );
      return undefined;
    }
  }
}

function renderHeader(source: CrmSyncSourcesEnum, isLoading?: boolean) {
  switch (source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return (
        <MappingHeader
          isLoading={isLoading}
          sourceImg={SalesforceImgSrc}
          sourceTitle={<Trans id="accounts-crm-dialog-sync-mapping-salesforce">Salesforce fields</Trans>}
          destinationImg={FrontImgSrc}
          destinationTitle={
            <Trans id="accounts-crm-dialog-sync-mapping-salesforce-front-fields">Front fields</Trans>
          }
        />
      );

    case CrmSyncSourcesEnum.HUBSPOT:
      return (
        <MappingHeader
          isLoading={isLoading}
          sourceImg={HubspotImgSrc}
          sourceTitle={<Trans id="accounts-crm-dialog-sync-mapping-hubspot">Hubspot fields</Trans>}
          destinationImg={FrontImgSrc}
          destinationTitle={
            <Trans id="accounts-crm-dialog-sync-mapping-hubspot-front-fields">Front fields</Trans>
          }
        />
      );
    default:
      return <StyledHeaderCellWrapperDiv />;
  }
}
